'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const IMEI_STORAGE_KEY = 'imei';

interface IMEIContextType {
  imei: string | null;
  setIMEI: (imei: string) => void;
  clearIMEI: () => void;
}

interface IMEIProviderProps {
  readonly children: React.ReactNode;
}

const IMEIContext = createContext<IMEIContextType | undefined>(undefined);

export function IMEIProvider(props: IMEIProviderProps) {
  const { children } = props;

  const [imei, setImei] = useState<string | null>(() => {
    if (globalThis.window === undefined) {
      return null;
    }

    return globalThis.window.sessionStorage.getItem(IMEI_STORAGE_KEY);
  });

  const setIMEI = useCallback((newImei: string) => {
    setImei(newImei);
    globalThis.window.sessionStorage.setItem(IMEI_STORAGE_KEY, newImei);
  }, []);

  const clearIMEI = useCallback(() => {
    setImei(null);
    globalThis.window.sessionStorage.removeItem(IMEI_STORAGE_KEY);
  }, []);

  const value = useMemo(() => ({ imei, setIMEI, clearIMEI }), [imei, setIMEI, clearIMEI]);

  return (
    <IMEIContext.Provider value={value}>
      {children}
    </IMEIContext.Provider>
  );
}

export function useIMEI() {
  const context = useContext(IMEIContext);
  if (context === undefined) {
    throw new Error('useIMEI must be used within an IMEIProvider');
  }
  return context;
}
